import React, { useEffect, useState } from 'react';
import '../../styles/DashboardEditor.css';
import { useNavigate } from 'react-router-dom';
import useStore from '../../store/useStore';
import { dashboardsAPI, dashboardTemplatesAPI } from '../../services/api';
import { loadDashboardData } from '../../utils/loadDashboardData';
import useModalOverlayClose from '../../hooks/useModalOverlayClose';

/**
 * Create a dashboard: name it and start blank or from a template built from
 * the company's own data. A blank dashboard opens straight into the editor
 * with the widget library showing.
 */
const NewDashboardDialog = ({ onClose }) => {
  const navigate = useNavigate();
  const companyId = useStore((state) => state.currentCompany?.id);
  const overlay = useModalOverlayClose(onClose);
  const [name, setName] = useState('');
  const [choice, setChoice] = useState('blank');
  const [templates, setTemplates] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!companyId) return;
    dashboardTemplatesAPI.list(companyId).then((r) => setTemplates(r?.success ? r.data.templates || [] : [])).catch(() => setTemplates([]));
  }, [companyId]);

  const chosen = templates.find((t) => t.key === choice);
  const finalName = name.trim() || (chosen ? chosen.name : '');
  const canCreate = finalName.length >= 3 && !busy;

  const create = async (e) => {
    e.preventDefault();
    if (!canCreate) return;
    try {
      setBusy(true);
      setError('');
      const res = choice === 'blank'
        ? await dashboardsAPI.create(finalName, companyId, false)
        : await dashboardTemplatesAPI.create(choice, companyId, finalName);
      if (!res?.success) throw new Error(res?.error || 'Could not create the dashboard');
      await loadDashboardData({ companyId, dashboardId: res.data.id });
      onClose();
      navigate(choice === 'blank' ? '/?edit=library' : '/');
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      setBusy(false);
    }
  };

  return (
    <div className="modal-overlay" {...overlay}>
      <div className="modal-content new-dashboard-dialog" role="dialog" aria-modal="true" aria-labelledby="new-dashboard-title" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 id="new-dashboard-title">New dashboard</h2>
          <button onClick={onClose} className="modal-close" aria-label="Close">&times;</button>
        </div>
        <form onSubmit={create}>
          <label className="inspector-field">
            <span>Name</span>
            <input autoFocus value={name} placeholder={chosen ? chosen.name : 'e.g. Operations, Month end'} onChange={(e) => setName(e.target.value)} />
          </label>
          <fieldset className="new-dashboard-choices">
            <legend>Start with</legend>
            <label className={`new-dashboard-choice ${choice === 'blank' ? 'selected' : ''}`}>
              <input type="radio" name="start" value="blank" checked={choice === 'blank'} onChange={() => setChoice('blank')} />
              <strong>A blank dashboard</strong>
              <small>Add widgets one at a time from the library.</small>
            </label>
            {templates.map((t) => (
              <label key={t.key} className={`new-dashboard-choice ${choice === t.key ? 'selected' : ''} ${t.widgets === 0 ? 'disabled' : ''}`}>
                <input type="radio" name="start" value={t.key} checked={choice === t.key} disabled={t.widgets === 0} onChange={() => setChoice(t.key)} />
                <strong>{t.name}</strong>
                <small>{t.description}</small>
                <small className="new-dashboard-count">{t.widgets === 0 ? 'No matching data yet' : `${t.widgets} widget${t.widgets === 1 ? '' : 's'} from your data`}</small>
              </label>
            ))}
          </fieldset>
          {error && <div className="error-message" role="alert">{error}</div>}
          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={!canCreate}>{busy ? 'Creating…' : 'Create dashboard'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewDashboardDialog;
